module Client.IllyAdmin.Actions.ConfigMetadata

open Types
open Elmish
open SafeStack.Client
open Illy.SafeStack.Client
open Fable.React
open Fable.React.Props
open Shared
open LinksManager.SharedConfigTypes.Monadic
open Client
open System

type Model =
    {
        Metadata: (string * ConfigMeta list) list
        FilteredMetadata: (string * ConfigMeta list) list
        CustomisedOnly: Check.Model
        SearchString: Text.Model
        DebounceSearch: SearchString Debounce.Model
        Loading: bool
        DownloadButton: DownloadControl.Model
        SettingCount: int
        VisibleCount: int
    }

type Msg =
    | LoadedData of (string * ConfigMeta list) list
    | CustomisedOnlyMsg of Check.Msg
    | ApiError of ApiError
    | SearchStringMsg of Text.Msg
    | DebounceSearchMsg of SearchString Debounce.Msg
    | RecalcVisible
    | Cancel
    | FileDownloaded of unit
    | DownloadButtonMsg of DownloadControl.Msg

module Commands =
    let getData () =
        Api.secureCall<IConfigMetadataIllyAdminApi,_,_,_> SecuredRoute.IllyAdmin (fun api -> api.GetMetadata) () LoadedData ApiError

    let download() =
        Api.downloadFile<IConfigMetadataIllyAdminApi,_,_>
            SecuredRoute.IllyAdmin
            (fun api -> api.DownloadMetadata)
            ()
            FileDownloaded
            ApiError

module Init =
    let searchString value =
        Text.CustomSettings.defaultOptions()
        |> Text.init "config-search" "Search String" (Some "Type here to search") 200. false value

    let customisedOnly () =
        {Check.CustomSettings.defaultOptions() with RenderInline = true}
        |> Check.init "customisedOnly" "Customised Settings Only" false false

    let downloadButton () =
        { DownloadControl.CustomSettings.defaultOptions() with Modifiers = [ FontAwesome.Size FontAwesome.X2 ] }
        |> DownloadControl.init (Some "Download as CSV") None

let init () =
    {
        Metadata = []
        FilteredMetadata = []
        Loading = true
        DownloadButton = Init.downloadButton ()
        SearchString = Init.searchString None
        CustomisedOnly = Init.customisedOnly()
        DebounceSearch = Debounce.init (TimeSpan.FromMilliseconds 300.) SearchString.empty
        VisibleCount = 0
        SettingCount = 0
    }, Commands.getData ()

let strMatch (a: string) (b: string) = a.Contains(b, StringComparison.InvariantCultureIgnoreCase)
let count (lst: list<string * ConfigMeta list>) =
    lst |> List.sumBy (fun (_,x) -> x.Length)

let private filter filterAtGroup filterAtItem metadata =
    [
        for (header, data) in metadata do
            if filterAtGroup header data then
                yield (header, data)
            else
                let subset =
                    data
                    |> List.filter (filterAtItem)

                if subset <> [] then
                    yield (header, subset)
    ]

let applyFilter (model: Model) =
    let customOnly = model.CustomisedOnly |> Check.toBool
    let textFilter = model.DebounceSearch.Output |> SearchString.isEmpty = false

    if customOnly = false && textFilter = false then
        model.Metadata
    else
        let str = model.DebounceSearch.Output |> SearchString.unwrap
        model.Metadata
        |> filter
            (fun header _ -> if textFilter then strMatch header str else false)
            (fun item ->
                let b1 = textFilter = false || strMatch item.Key str
                let b2 = customOnly = false || match item.ConfiguredValue with Some x when x <> item.DefaultValue -> true | _ -> false
                b1 && b2
            )

let update msg (model: Model) =
    match msg with
    | ApiError err ->
        { model with Loading = false; DownloadButton = DownloadControl.resetDownloading model.DownloadButton }, err |> AppError.ApiError |> ActionEvent.AppError |> SideEffect.Event
    | Msg.Cancel -> model, ActionEvent.BackToGrid None |> SideEffect.Event
    | FileDownloaded _ ->
        { model with DownloadButton = DownloadControl.resetDownloading model.DownloadButton }, SideEffect.none
    | DownloadButtonMsg dMsg ->
        let m, eff = DownloadControl.update dMsg model.DownloadButton
        let newEff =
            match eff with
            | SideEffect.Cmd msg ->
                msg |> Cmd.map DownloadButtonMsg |> SideEffect.Cmd
            | SideEffect.Event (DownloadControl.Clicked) ->
                Commands.download () |> SideEffect.Cmd
        { model with DownloadButton = m }, newEff
    | LoadedData data ->
        let c = count data
        { model with
            Loading = false
            Metadata = data
            FilteredMetadata = data
            SettingCount = c
            VisibleCount = c
        }, SideEffect.none
    | CustomisedOnlyMsg cmsg ->
        let cmod, ccmd = Check.update cmsg model.CustomisedOnly
        { model with CustomisedOnly = cmod }, Cmd.batch [
            Cmd.map CustomisedOnlyMsg ccmd
            Cmd.ofMsg RecalcVisible
        ] |> SideEffect.Cmd
    | SearchStringMsg cMsg ->
        let m, c = Text.update cMsg model.SearchString
        let hasSearchStrChanged = Text.toString m <> Text.toString model.SearchString
        {model with 
            SearchString = m
        }, Cmd.batch [
            yield Cmd.map SearchStringMsg c
            if hasSearchStrChanged then yield Debounce.inputCmd (m |> Text.toString |> SearchString.ofString) DebounceSearchMsg
        ] |> SideEffect.Cmd
    | DebounceSearchMsg dMsg -> 
        let m, c = 
            Debounce.updateWithCmd
                dMsg
                DebounceSearchMsg
                model.DebounceSearch
                (fun x -> 
                    { model with
                        DebounceSearch = x 
                    })
                (fun _ -> Cmd.ofMsg RecalcVisible)
        m, SideEffect.Cmd c
    | RecalcVisible ->
        let visible = applyFilter model
        {
            model with
                FilteredMetadata = visible
                VisibleCount = count visible
        }, SideEffect.none

let renderGrid heading (group: ConfigMeta list) =
    section [] [
        h4 [] [ str heading ]
        jQueryUI.simpleTable
            "No config settings"
            [
                jQueryUI.ColumnMetaData.createWithHeader [ str "Name" ] (fun m -> [ str m.Key ])
                jQueryUI.ColumnMetaData.createWithHeader [ str "Type" ] (fun m -> [ m.DataType |> ConfigDataType.toString |> str ])
                jQueryUI.ColumnMetaData.createWithHeader [ str "Default" ] (fun m -> [
                    let defaultWithTooltip hostElement maxLength (text: string) =
                        let truncated =
                            if text.Length > maxLength - 3 then
                                $"%s{text.Substring(0, 61)}..."
                            else
                                text

                        hostElement [ str truncated ]
                        |>  if text <> truncated then
                                Tooltip.createInteractive [ str text ]
                            else
                                id

                    yield defaultWithTooltip (span []) 64 (m.DefaultValue)
                    match m.ConfiguredValue with
                    | None -> ()
                    | Some v ->
                        yield str " --> "
                        yield defaultWithTooltip (b []) 64 v
                ])
                jQueryUI.ColumnMetaData.createWithHeader [ str "Details" ] (fun m -> [ str m.Explanation ])
            ]
            false
            group
    ]

let view model dispatch =
    [
        div [ Id "config-metadata" ] [
            yield section [ ClassName "gridControls" ] [
                div [ ClassName "search-container grow" ] [
                    Text.textInput false model.SearchString (SearchStringMsg >> dispatch)
                ]
                div [ ClassName "controls"] [
                    Check.check false model.CustomisedOnly (CustomisedOnlyMsg >> dispatch)
                ]
                
            ]

            yield DownloadControl.button model.Loading (DownloadButtonMsg >> dispatch) model.DownloadButton

            if model.SettingCount = model.VisibleCount then
                yield p [] [ str (model.SettingCount.ToString()); str " settings" ]
            else
                yield p [] [ str (model.VisibleCount.ToString()); str " of "; str (model.SettingCount.ToString()); str " settings visible" ]

            for (header, data) in model.FilteredMetadata |> List.sortBy fst do
                yield renderGrid header data
        ]
    ]

let metaData =
    {
        Key = AdminActionKey.BrowseConfigMetadata
        Title = "Config Metadata"
        Description = "Browse a list of all configs in the system with metadata, and their configured values"
        Group = ActionGroup.Config
    }